import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import colors from 'common/constants/colors';
import { NEW_SIG_DRAFT_ID, SigLengthLimits } from 'enums/SigManagement.enum';
import { sigActions } from 'store/slices/sigSlice';
import {
  clearSigDetail,
  commitSigAssociations,
  createSigWithAssociations,
  loadSigDetail,
  upsertSigAssociations,
} from 'store/thunks/sigThunks';

import {
  applyDraftToCatalogItems,
  applyDraftToDirections,
  describeStagedChanges,
} from '../utils/applySigDraft';
import ConfirmSigStatusChangeDialog from './ConfirmSigStatusChangeDialog';
import PendingSigChangesCard from './PendingSigChangesCard';
import SigCatalogAssociationsPanel from './SigCatalogAssociationsPanel';
import SigDirectionAssociationsPanel from './SigDirectionAssociationsPanel';
import SigSummarySection from './SigSummarySection';

/**
 * Everything about one sig's associations, on one screen.
 *
 * Near full-screen on purpose. The two association lists are read side by side and compared, and a
 * medium dialog either stacks them — losing the comparison — or squeezes each list into a column
 * too narrow for a direction to fit on one line.
 *
 * The header and footer stay put while the middle scrolls, so the sig being managed and the commit
 * button are never scrolled out of reach of a long list.
 *
 * Nothing in the middle is written until Commit changes. The exception is Deactivate, which is a
 * single decisive act with its own confirmation and its own consequences for the sig's drug
 * approvals — folding it into a changeset would bury that warning behind a second button.
 *
 * The same dialog also creates a sig, which is why `sigRow` is optional. Add New used to open a
 * form that offered directions and nothing else, so a sig's catalog approvals could only be added
 * by finding the sig again afterwards and opening this dialog — the create path was missing half
 * the screen for no reason other than that it had been written first. Creating differs from editing
 * only in having nothing loaded to lay the draft over, so it is a mode here rather than a second
 * component that would drift from this one.
 */
const SigDetailDialog = ({ sigRow, isCreating, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const sigDetail = useSelector(({ sigs }) => sigs.sigDetail);
  const sigDraft = useSelector(({ sigs }) => sigs.sigDraft);
  const [isEditingSig, setIsEditingSig] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [commitError, setCommitError] = useState(undefined);

  // A sig being created has no row and no id, so the draft is held under a sentinel of its own —
  // see NEW_SIG_DRAFT_ID. Everything below then keys off `sigId` exactly as the edit path does.
  const { sigId, sig, sigStatus } = isCreating
    ? { sigId: NEW_SIG_DRAFT_ID, sig: undefined, sigStatus: true }
    : sigRow;
  // Until the response for THIS sig lands, the store may still hold the previously opened sig's
  // rows. Treated as loading, so reopening a second sig cannot flash the first one's associations.
  // Nothing is fetched while creating, so there is nothing to wait for.
  const showingThisSig = isCreating || (sigDetail.sigId === sigId && !sigDetail.loading);
  // A commit submits the sig's whole direction list, and the server sets inactive any direction
  // absent from it. So nothing may be written until every direction is actually in hand. A sig
  // being created has none, which is trivially all of them.
  const holdsEveryDirection =
    isCreating || (showingThisSig && sigDetail.directions.length >= sigDetail.directionCount);
  const draftBelongsToThisSig = sigDraft.sigId === sigId;
  // Falls back to a string rather than undefined: a sig being created has no wording to start from,
  // and the length check below reads this directly.
  const editedSigText = (draftBelongsToThisSig ? (sigDraft.sigText ?? sig) : sig) ?? '';

  const directions = useMemo(
    () => (showingThisSig ? applyDraftToDirections(sigDetail.directions, sigDraft) : []),
    [showingThisSig, sigDetail.directions, sigDraft]
  );
  const catalogItems = useMemo(
    () => (showingThisSig ? applyDraftToCatalogItems(sigDetail.approvedDrugs, sigDraft) : []),
    [showingThisSig, sigDetail.approvedDrugs, sigDraft]
  );
  // Guarded on the draft belonging to this sig for the same reason the two staged lists below are:
  // on the first render after opening, the draft may still be the previously opened sig's, and this
  // would render its pending changes under the sig now on screen.
  const stagedChanges = useMemo(
    () =>
      showingThisSig && draftBelongsToThisSig
        ? describeStagedChanges({
            sig,
            directions: sigDetail.directions,
            catalogItems: sigDetail.approvedDrugs,
            sigDraft,
            isCreating,
          })
        : [],
    [
      showingThisSig,
      draftBelongsToThisSig,
      isCreating,
      sig,
      sigDetail.directions,
      sigDetail.approvedDrugs,
      sigDraft,
    ]
  );

  // Guarded, because on the very first render the draft may still be the previously opened sig's —
  // startSigDraft has not run yet. Reading it unguarded would let another sig's staged directions
  // count toward this one.
  const stagedNewDirections = draftBelongsToThisSig ? sigDraft.newDirections : [];
  const stagedNewCatalogItems = draftBelongsToThisSig ? sigDraft.newCatalogItems : [];

  const trimmedSigLength = editedSigText.trim().length;
  const isSigLengthValid =
    trimmedSigLength >= SigLengthLimits.SIG_MIN && trimmedSigLength <= SigLengthLimits.SIG_MAX;
  const isRenamingSig = !isCreating && editedSigText !== sig;
  /**
   * Whether the sig's wording is one the server will take.
   *
   * Enforced when it is being written — created, or renamed — and not otherwise. The gate used to
   * be on the create branch alone, so an edit could paste 200 characters, watch the field turn red
   * and still commit, and the server refused it.
   *
   * Conditional on a rename for the reason the server's own guard is: the resolve path had no
   * maximum until now and created sigs from directions of up to 510 characters. Enforced
   * unconditionally, this would lock every other change to one of those rows — including the
   * direction edits that are the way to make them useful again.
   */
  const isSigTextAcceptable = isCreating || isRenamingSig ? isSigLengthValid : true;
  // A sig with no directions matches nothing, so it can never be reached again — the old Add form
  // required one for the same reason.
  //
  // Otherwise read straight off the list the card renders, so the button and the summary cannot
  // disagree about whether there is anything to commit.
  const hasChangesToCommit =
    isSigTextAcceptable && (isCreating ? !isEmpty(stagedNewDirections) : !isEmpty(stagedChanges));
  // Discard is gated on there being something staged, NOT on that something being submittable. A
  // new sig with wording but no direction yet cannot be created, and if Discard followed the same
  // gate there would be no way to clear the field.
  const hasAnythingToDiscard = isCreating
    ? !isEmpty(editedSigText) || !isEmpty(stagedNewDirections) || !isEmpty(stagedNewCatalogItems)
    : hasChangesToCommit;

  useEffect(() => {
    dispatch(sigActions.startSigDraft({ sigId }));

    // Nothing to fetch for a sig that does not exist, and asking for one would 404 on a sentinel id.
    if (isCreating) {
      return undefined;
    }

    dispatch(loadSigDetail({ sigId }));

    // Only the loaded rows are dropped on close. The draft is deliberately left, so reopening the
    // same sig finds the staged changes where they were.
    return () => dispatch(clearSigDetail());
  }, [dispatch, isCreating, sigId]);

  const undoHandlers = {
    sigText: () => dispatch(sigActions.unstageSigText()),
    newDirection: ({ draftId }) => dispatch(sigActions.unstageNewDirection({ draftId })),
    newCatalogItem: ({ catalogId }) => dispatch(sigActions.unstageNewCatalogItem({ catalogId })),
    directionStatus: ({ directionId }) =>
      dispatch(sigActions.unstageDirectionStatus({ directionId })),
    catalogStatus: ({ catalogId }) => dispatch(sigActions.unstageCatalogStatus({ catalogId })),
  };

  const handleUndo = (undo) => undoHandlers[undo.kind]?.(undo);

  /**
   * What to put in front of the reviewer when a write fails.
   *
   * Three quite different failures, in the order of how much they say:
   *
   * 1. The mutation was refused and said why. Shown verbatim: the backend's message is the most
   *    specific thing available — it names the sig that already holds the direction, or the limit
   *    that was exceeded, and it states the way out.
   *
   *    Nothing is added to it here. The screen used to append the conflicting sig from
   *    `extensions.currentSig`, which never arrives: the gatekeeper rebuilds every error it
   *    forwards and keeps only requestId, i18nCode and errorData. The message is the only field
   *    that survives the hop, so it is where the backend puts the detail.
   * 2. The write half-landed. The sig and its directions are on one database and the catalog
   *    approvals on another, so only one half may have gone in. Saying "nothing was applied" here
   *    would be untrue.
   * 3. Nothing came back at all, so only the shape of the failure is known.
   */
  const describeCommitFailure = (response) => {
    if (response?.error) {
      return response.error;
    }

    if (response?.catalogOutcome?.applied === false) {
      const reason = response.catalogOutcome.error ?? 'the catalog could not be reached';

      return isCreating
        ? `The sig and its directions were created, but its drug approvals were not: ${reason}. Open the sig from the list to add them.`
        : `The sig and its directions were saved, but its drug approvals were not: ${reason}`;
    }

    return isCreating
      ? 'The sig could not be created. Nothing was saved — please try again.'
      : 'The changes could not be committed. Nothing was applied — please try again.';
  };

  // The directions travel with the flip because the server cascades an explicit switch-off onto
  // them, and omitting them here would set every direction on the sig inactive.
  const handleConfirmStatusChange = () => {
    setSaving(true);
    setCommitError(undefined);
    // Closed either way: on success the whole dialog goes, and on failure the confirmation would
    // otherwise sit on top of the error message explaining what went wrong.
    setShowStatusConfirm(false);

    return dispatch(
      upsertSigAssociations({
        input: {
          sigId,
          sig,
          sigStatus: !sigStatus,
          associatedDirections: sigDetail.directions.map(({ directionId, direction }) => ({
            directionId,
            direction,
            directionStatus: !sigStatus,
          })),
        },
      })
    )
      .then((response) => {
        // `success`, not merely a resolved response. The mutation reports a semantic failure as
        // `{ success: false }` without throwing, so `if (response)` announced "Sig deactivated" for
        // a deactivation that had not happened.
        if (response?.success) {
          // The draft goes with it. This write bypasses the changeset and cascades onto every
          // direction the sig has, so anything still staged was decided against a state that no
          // longer exists — reopening the sig would lay it over fresh server data and Commit would
          // write a change nobody asked for in this session.
          dispatch(sigActions.clearSigDraft());
          enqueueSnackbar(sigStatus ? 'Sig deactivated' : 'Sig activated', { variant: 'success' });
          onClose();

          return;
        }

        // The backend's own message when it refused and said why; otherwise the shape of it.
        setCommitError(
          response?.error ??
            response?.catalogOutcome?.error ??
            `The sig could not be ${sigStatus ? 'deactivated' : 'activated'}. Please try again.`
        );
      })
      .finally(() => setSaving(false));
  };

  const handleCommit = () => {
    setSaving(true);
    setCommitError(undefined);

    dispatch(isCreating ? createSigWithAssociations() : commitSigAssociations({ sigRow }))
      .then((response) => {
        if (response?.success) {
          enqueueSnackbar(isCreating ? 'Sig created' : 'Changes committed', { variant: 'success' });
          onClose();

          return;
        }

        // The sig half landed and only the approvals did not. Creating tells the user so and lets
        // them go: the sig now exists, so leaving the draft in place would put a Create button in
        // front of them that can only fail on the sig's own canonical form.
        if (isCreating && response?.catalogOutcome?.applied === false) {
          dispatch(sigActions.clearSigDraft());
          enqueueSnackbar(describeCommitFailure(response), { variant: 'warning' });
          onClose();

          return;
        }

        // Shown in the dialog, not a toast, and the draft is kept: nearly everything refused here
        // is something the reviewer fixes in this form and submits again.
        setCommitError(describeCommitFailure(response));
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth={false}
        onClose={onClose}
        PaperProps={{
          sx: {
            // Near full-screen with a margin, rather than fullScreen: the margin keeps the page
            // behind it visible, so the dialog reads as something opened over the list rather than
            // as having navigated away from it.
            width: 'calc(100% - 48px)',
            height: 'calc(100% - 48px)',
            maxHeight: 'none',
            m: 3,
            borderRadius: 2,
            bgcolor: colors.dialogSurfaceGrey,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: colors.white,
            py: 2,
          }}
        >
          {/* A span, not the default heading element: DialogTitle already renders an h2, and an
              h6 inside it is invalid nesting. */}
          <Typography component='span' variant='h6' sx={{ fontWeight: 600 }}>
            {isCreating ? 'Add Sig Association' : 'Manage Sig Association'}
          </Typography>
          <IconButton aria-label='Close' onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: 3, py: 2.5 }}>
          <SigSummarySection
            sig={sig}
            sigStatus={sigStatus}
            editedSigText={editedSigText}
            isEditing={isEditingSig}
            isCreating={isCreating}
            canDeactivate={holdsEveryDirection}
            saving={saving}
            onStartEditing={() => setIsEditingSig(true)}
            onStopEditing={() => setIsEditingSig(false)}
            onEditedSigTextChange={(sigText) => dispatch(sigActions.stageSigText({ sigText }))}
            onToggleStatus={() => setShowStatusConfirm(true)}
          />

          {/* Side by side on wide screens, which is the whole reason the dialog is this size. They
              stack below md so the layout survives a narrow window without overlapping. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
              alignItems: 'stretch',
            }}
          >
            <SigDirectionAssociationsPanel
              directions={directions}
              directionCount={showingThisSig ? sigDetail.directionCount : 0}
              newDirections={stagedNewDirections}
              loading={!showingThisSig}
              isCreating={isCreating}
              onStageStatus={({ directionId, isActive }) =>
                dispatch(sigActions.stageDirectionStatus({ directionId, isActive }))
              }
              onStageNew={(direction) =>
                dispatch(sigActions.stageNewDirection({ draftId: uuidv4(), direction }))
              }
              onUnstageNew={(draftId) => dispatch(sigActions.unstageNewDirection({ draftId }))}
            />

            <SigCatalogAssociationsPanel
              catalogItems={catalogItems}
              catalogItemCount={showingThisSig ? sigDetail.approvedDrugCount : 0}
              newCatalogItems={stagedNewCatalogItems}
              loading={!showingThisSig}
              isCreating={isCreating}
              onStageStatus={({ catalogId, isActive }) =>
                dispatch(sigActions.stageCatalogStatus({ catalogId, isActive }))
              }
              onStageNew={(catalogItem) =>
                dispatch(sigActions.stageNewCatalogItem({ catalogItem }))
              }
              onUnstageNew={(catalogId) =>
                dispatch(sigActions.unstageNewCatalogItem({ catalogId }))
              }
            />
          </Box>

          <PendingSigChangesCard changes={stagedChanges} onUndo={handleUndo} />
        </DialogContent>

        <Divider />

        {/* A footer landmark, not a bare div: it holds the commit controls and the reason a commit
            was refused, so it is somewhere a screen reader user can jump to rather than something
            they have to arrive at by walking the whole dialog. */}
        <DialogActions component='footer' sx={{ bgcolor: colors.white, px: 3, py: 2, gap: 1 }}>
          {/* In the sticky footer, beside the button that produced it. At the bottom of the
              scrolling middle it was below the fold on a sig with a few associations: the commit
              appeared to do nothing, and the reason was somewhere the reviewer had to go looking
              for. The footer never scrolls away, so it is read where the click happened.

              flexGrow so it takes the space the buttons do not, and the buttons stay put whether
              there is an error or not. */}
          {commitError && (
            <Alert severity='error' sx={{ flexGrow: 1, mr: 1, py: 0.25 }}>
              {commitError}
            </Alert>
          )}

          {/* No second Close here. The X in the header already closes the dialog, and two controls
              with the same name is one to skip past for anyone using a screen reader. */}
          <Button
            variant='text'
            color='inherit'
            disabled={!hasAnythingToDiscard || saving}
            onClick={() => {
              // Kept pointing at this sig rather than reset outright, so the dialog still
              // recognises what is staged next — see discardSigDraft.
              dispatch(sigActions.discardSigDraft({ sigId }));
              setIsEditingSig(false);
            }}
          >
            Discard changes
          </Button>
          <LoadingButton
            variant='contained'
            disableElevation
            size='large'
            loading={saving}
            startIcon={<CheckIcon />}
            disabled={!hasChangesToCommit || !holdsEveryDirection}
            onClick={handleCommit}
          >
            {isCreating ? 'Create sig' : 'Commit changes'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {showStatusConfirm && (
        <ConfirmSigStatusChangeDialog
          sig={sig}
          isActive={sigStatus}
          directionCount={sigDetail.directionCount}
          approvedDrugCount={sigDetail.approvedDrugCount}
          loading={saving}
          onCancel={() => setShowStatusConfirm(false)}
          onConfirm={handleConfirmStatusChange}
        />
      )}
    </>
  );
};

export default SigDetailDialog;

SigDetailDialog.propTypes = {
  // Absent when creating, which is the only time it may be. `isCreating` takes precedence: with it
  // set, no row is read at all.
  sigRow: PropTypes.shape({
    sigId: PropTypes.string.isRequired,
    sig: PropTypes.string.isRequired,
    sigStatus: PropTypes.bool,
  }),
  isCreating: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

SigDetailDialog.defaultProps = {
  sigRow: undefined,
  isCreating: false,
};
